import { FilterValidationValues } from 'common/models/filter.model';
import { RootState } from 'common/store';
import { selectCommonFilters } from 'common/store/slices/common-filters.slice';
import { useSelector } from 'react-redux';
import usePageFilters from './usePageFilters';

interface UseFilterValidationProps {
  datasetId: string;
  filters?: FilterValidationValues;
  validateAllFilters?: boolean;
}

/**
 * Custom hook to validate if one or more filters have a value.
 *
 * @param {string} props.datasetId - The ID of the dataset to validate filters against.
 * @param {string | string[]} props.filters - The filter or array of filter names to validate.
 * @param {boolean} props.validateAllFilters - If `true`, all current filters must have a value to be considered valid.
 * @returns {boolean} - Returns `true` if all filters are valid, otherwise `false`.
 */
function useFilterValidation({
  datasetId,
  filters,
  validateAllFilters,
}: UseFilterValidationProps): boolean {
  const { pageFilters } = usePageFilters({ datasetId });

  const currentFilterValues =
    useSelector((state: RootState) => selectCommonFilters(state, datasetId)) ?? {};

  if (validateAllFilters) {
    const filtersWithValidations =
      pageFilters
        ?.filter((filter) => filter.requiredFilters)
        .map((filter) => {
          if (Array.isArray(filter.requiredFilters)) {
            return filter.requiredFilters;
          }

          return [filter.requiredFilters];
        })
        .flat() ?? [];

    return !!filtersWithValidations.every((filter) => {
      return currentFilterValues[filter as string]?.value !== undefined;
    });
  }

  if (!filters) {
    return true;
  }

  const filtersArray = Array.isArray(filters) ? filters : [filters];

  const allFiltersValid = filtersArray.every((filter) => {
    return currentFilterValues[filter as string]?.value !== undefined;
  });

  return allFiltersValid;
}

export default useFilterValidation;
